import {
  FeatureSubscriptionPaymentCycle,
  FeatureSubscriptionProductCode,
  FeatureSubscriptionType,
} from 'queries';

import { NodeEnvironment } from 'utils/enum';

export const FEATURE_SUBSCRIPTION_ADD_ONS = [
  FeatureSubscriptionProductCode.AdditionalSeats,
  FeatureSubscriptionProductCode.AdditionalSeatsTest,
  FeatureSubscriptionProductCode.ImplementationFee,
  FeatureSubscriptionProductCode.ImplementationFeeTest,
];

export function getNameForProduct(stripeProductId: FeatureSubscriptionProductCode): string {
  let name = 'None';

  switch (stripeProductId) {
    case FeatureSubscriptionProductCode.TrialPlan:
    case FeatureSubscriptionProductCode.TrialPlanTest:
      name = 'Trial';
      break;

    case FeatureSubscriptionProductCode.LitePlan:
    case FeatureSubscriptionProductCode.LitePlanTest:
      name = 'Lite';
      break;

    case FeatureSubscriptionProductCode.SoloPlan:
    case FeatureSubscriptionProductCode.SoloPlanTest:
      name = 'Solo';
      break;

    case FeatureSubscriptionProductCode.SmallFirmPlan:
    case FeatureSubscriptionProductCode.SmallFirmPlanTest:
      name = 'Small Firm';
      break;

    case FeatureSubscriptionProductCode.MidLargeFirmPlan:
    case FeatureSubscriptionProductCode.MidLargeFirmPlanTest:
      name = 'Mid-Large Firm';
      break;

    case FeatureSubscriptionProductCode.PremiumPlan:
    case FeatureSubscriptionProductCode.PremiumPlanTest:
      name = 'Premium';
      break;
  }

  return name;
}

export function getNameForFeatureSubscriptionType(
  featureSubscriptionType: FeatureSubscriptionType,
): string {
  let name = 'None';

  switch (featureSubscriptionType) {
    case FeatureSubscriptionType.Trial:
      name = 'Trial';
      break;

    case FeatureSubscriptionType.Lite:
      name = 'Lite';
      break;

    case FeatureSubscriptionType.Solo:
      name = 'Solo';
      break;

    case FeatureSubscriptionType.Professional:
      name = 'Professional';
      break;

    case FeatureSubscriptionType.ProfessionalPlus:
      name = 'Professional Plus';
      break;

    case FeatureSubscriptionType.SmallFirm:
      name = 'Small Firm';
      break;

    case FeatureSubscriptionType.MidLargeFirm:
      name = 'Mid-Large Firm';
      break;

    case FeatureSubscriptionType.Premium:
      name = 'Premium';
      break;

    case FeatureSubscriptionType.Unlimited:
      name = 'Unlimited';
      break;
  }

  return name;
}

export function getPlanOptionIndex(planOption: FeatureSubscriptionProductCode | null) {
  if (planOption === FeatureSubscriptionProductCode.TrialPlan) {
    return 1;
  } else if (
    planOption === FeatureSubscriptionProductCode.SoloPlanLegacy ||
    planOption === FeatureSubscriptionProductCode.SoloPlan ||
    planOption === FeatureSubscriptionProductCode.LitePlan
  ) {
    return 2;
  } else if (
    // pro and pro plus here because Mid / Large is still more expensive
    planOption === FeatureSubscriptionProductCode.SmallFirmPlan ||
    planOption === FeatureSubscriptionProductCode.ProfessionalPlan ||
    planOption === FeatureSubscriptionProductCode.ProfessionalPlusPlan
  ) {
    return 3;
  } else if (planOption === FeatureSubscriptionProductCode.MidLargeFirmPlan) {
    return 4;
  } else {
    return 1;
  }
}

export function getProductCodeForProduction(
  productCode: FeatureSubscriptionProductCode,
): FeatureSubscriptionProductCode | null {
  let productCodeProd = null;

  switch (productCode) {
    case FeatureSubscriptionProductCode.TrialPlan:
    case FeatureSubscriptionProductCode.TrialPlanTest:
      productCodeProd = FeatureSubscriptionProductCode.TrialPlan;
      break;
    case FeatureSubscriptionProductCode.LitePlan:
    case FeatureSubscriptionProductCode.LitePlanTest:
      productCodeProd = FeatureSubscriptionProductCode.LitePlan;
      break;

    case FeatureSubscriptionProductCode.SoloPlan:
    case FeatureSubscriptionProductCode.SoloPlanTest:
      productCodeProd = FeatureSubscriptionProductCode.SoloPlan;
      break;

    case FeatureSubscriptionProductCode.ProfessionalPlan:
    case FeatureSubscriptionProductCode.ProfessionalPlanTest:
      productCodeProd = FeatureSubscriptionProductCode.ProfessionalPlan;
      break;

    case FeatureSubscriptionProductCode.ProfessionalPlusPlan:
    case FeatureSubscriptionProductCode.ProfessionalPlusPlanTest:
      productCodeProd = FeatureSubscriptionProductCode.ProfessionalPlusPlan;
      break;

    case FeatureSubscriptionProductCode.SmallFirmPlan:
    case FeatureSubscriptionProductCode.SmallFirmPlanTest:
      productCodeProd = FeatureSubscriptionProductCode.SmallFirmPlan;
      break;

    case FeatureSubscriptionProductCode.MidLargeFirmPlan:
    case FeatureSubscriptionProductCode.MidLargeFirmPlanTest:
      productCodeProd = FeatureSubscriptionProductCode.MidLargeFirmPlan;
      break;

    case FeatureSubscriptionProductCode.PremiumPlan:
    case FeatureSubscriptionProductCode.PremiumPlanTest:
      productCodeProd = FeatureSubscriptionProductCode.PremiumPlan;
      break;
  }

  return productCodeProd;
}

export function getProductCodeForProductType(productType: FeatureSubscriptionType) {
  switch (productType) {
    case FeatureSubscriptionType.Trial:
      return getProductCodeForProduction(FeatureSubscriptionProductCode.TrialPlan);
    case FeatureSubscriptionType.Lite:
      return getProductCodeForProduction(FeatureSubscriptionProductCode.LitePlan);
    case FeatureSubscriptionType.Solo:
      return getProductCodeForProduction(FeatureSubscriptionProductCode.SoloPlan);
    case FeatureSubscriptionType.Professional:
      return getProductCodeForProduction(FeatureSubscriptionProductCode.ProfessionalPlan);
    case FeatureSubscriptionType.ProfessionalPlus:
      return getProductCodeForProduction(FeatureSubscriptionProductCode.ProfessionalPlusPlan);
    case FeatureSubscriptionType.SmallFirm:
      return getProductCodeForProduction(FeatureSubscriptionProductCode.SmallFirmPlan);
    case FeatureSubscriptionType.MidLargeFirm:
      return getProductCodeForProduction(FeatureSubscriptionProductCode.MidLargeFirmPlan);
    case FeatureSubscriptionType.Premium:
      return getProductCodeForProduction(FeatureSubscriptionProductCode.PremiumPlan);
    case FeatureSubscriptionType.Unlimited:
      return getProductCodeForProduction(FeatureSubscriptionProductCode.UnlimitedPlan);
    default:
      return null;
  }
}

// NOTE - this also is defined in the API (conditions must be kept in sync)
export function getProductCodeForCurrentNodeEnvironment(
  productCode: FeatureSubscriptionProductCode,
) {
  // Current
  if (productCode === FeatureSubscriptionProductCode.TrialPlan) {
    return process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV ||
      process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT
      ? FeatureSubscriptionProductCode.TrialPlanTest
      : productCode;
  }

  if (productCode === FeatureSubscriptionProductCode.LitePlan) {
    return process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV ||
      process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT
      ? FeatureSubscriptionProductCode.LitePlanTest
      : productCode;
  }

  if (productCode === FeatureSubscriptionProductCode.SoloPlan) {
    return process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV ||
      process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT
      ? FeatureSubscriptionProductCode.SoloPlanTest
      : productCode;
  }

  if (productCode === FeatureSubscriptionProductCode.ProfessionalPlan) {
    return process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV ||
      process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT
      ? FeatureSubscriptionProductCode.ProfessionalPlanTest
      : productCode;
  }

  if (productCode === FeatureSubscriptionProductCode.ProfessionalPlusPlan) {
    return process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV ||
      process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT
      ? FeatureSubscriptionProductCode.ProfessionalPlusPlanTest
      : productCode;
  }

  if (productCode === FeatureSubscriptionProductCode.SmallFirmPlan) {
    return process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV ||
      process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT
      ? FeatureSubscriptionProductCode.SmallFirmPlanTest
      : productCode;
  }

  if (productCode === FeatureSubscriptionProductCode.MidLargeFirmPlan) {
    return process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV ||
      process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT
      ? FeatureSubscriptionProductCode.MidLargeFirmPlanTest
      : productCode;
  }

  if (productCode === FeatureSubscriptionProductCode.PremiumPlan) {
    return process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV ||
      process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT
      ? FeatureSubscriptionProductCode.PremiumPlanTest
      : productCode;
  }

  // Subscription line items
  if (productCode === FeatureSubscriptionProductCode.ImplementationFee) {
    return process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV ||
      process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT
      ? FeatureSubscriptionProductCode.ImplementationFeeTest
      : productCode;
  }

  if (productCode === FeatureSubscriptionProductCode.AdditionalSeats) {
    return process.env.REACT_APP_NODE_ENV === NodeEnvironment.DEV ||
      process.env.REACT_APP_NODE_ENV === NodeEnvironment.UAT
      ? FeatureSubscriptionProductCode.AdditionalSeatsTest
      : productCode;
  }
}

export function getSeatQuantityForProduct(stripeProductId: FeatureSubscriptionProductCode): number {
  let seatQuantity = 1;

  switch (stripeProductId) {
    case FeatureSubscriptionProductCode.TrialPlan:
    case FeatureSubscriptionProductCode.TrialPlanTest:
      seatQuantity = 1;
      break;
    case FeatureSubscriptionProductCode.LitePlan:
    case FeatureSubscriptionProductCode.LitePlanTest:
    case FeatureSubscriptionProductCode.SoloPlan:
    case FeatureSubscriptionProductCode.SoloPlanTest:
      seatQuantity = 2;
      break;

    case FeatureSubscriptionProductCode.ProfessionalPlan:
    case FeatureSubscriptionProductCode.ProfessionalPlanTest:
    case FeatureSubscriptionProductCode.SmallFirmPlan:
    case FeatureSubscriptionProductCode.SmallFirmPlanTest:
      seatQuantity = 4;
      break;

    case FeatureSubscriptionProductCode.ProfessionalPlusPlan:
    case FeatureSubscriptionProductCode.ProfessionalPlusPlanTest:
    case FeatureSubscriptionProductCode.MidLargeFirmPlan:
    case FeatureSubscriptionProductCode.MidLargeFirmPlanTest:
    case FeatureSubscriptionProductCode.UnlimitedPlan:
    case FeatureSubscriptionProductCode.UnlimitedPlanTest:
    case FeatureSubscriptionProductCode.PremiumPlan:
    case FeatureSubscriptionProductCode.PremiumPlanTest:
      seatQuantity = 8;
      break;
  }

  return seatQuantity;
}

// TODO TD - this function is no longer dependent on subscription type
export function getTotalCredits(
  featureSubscriptionType: FeatureSubscriptionProductCode,
  featureSubscriptionPayCycle: FeatureSubscriptionPaymentCycle,
): number {
  let totalCredits = 0;
  switch (featureSubscriptionType) {
    case FeatureSubscriptionProductCode.TrialPlan:
    case FeatureSubscriptionProductCode.TrialPlanTest:
    case FeatureSubscriptionProductCode.LitePlan:
    case FeatureSubscriptionProductCode.LitePlanTest:
    case FeatureSubscriptionProductCode.SoloPlan:
    case FeatureSubscriptionProductCode.SoloPlanTest:
      totalCredits =
        featureSubscriptionPayCycle === FeatureSubscriptionPaymentCycle.Monthly ? 5 : 60;
      break;

    case FeatureSubscriptionProductCode.ProfessionalPlan:
    case FeatureSubscriptionProductCode.ProfessionalPlanTest:
    case FeatureSubscriptionProductCode.SmallFirmPlan:
    case FeatureSubscriptionProductCode.SmallFirmPlanTest:
      totalCredits =
        featureSubscriptionPayCycle === FeatureSubscriptionPaymentCycle.Monthly ? 15 : 180;
      break;

    case FeatureSubscriptionProductCode.ProfessionalPlusPlan:
    case FeatureSubscriptionProductCode.ProfessionalPlusPlanTest:
    case FeatureSubscriptionProductCode.MidLargeFirmPlan:
    case FeatureSubscriptionProductCode.MidLargeFirmPlanTest:
      totalCredits =
        featureSubscriptionPayCycle === FeatureSubscriptionPaymentCycle.Monthly ? 30 : 360;
      break;

    case FeatureSubscriptionProductCode.PremiumPlan:
    case FeatureSubscriptionProductCode.PremiumPlanTest:
      totalCredits =
        featureSubscriptionPayCycle === FeatureSubscriptionPaymentCycle.Monthly ? 100 : 1200;
      break;
  }

  return totalCredits;
}
